import { sprintf } from 'sprintf-js'
import siteConfig from '@/config.json'

const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const months = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
]
const shortMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const dateToYYYYMMDD = (date) => {
  return sprintf('%04d-%02d-%02d', date.getFullYear(), date.getMonth() + 1, date.getDate())
}

const yyyymmddToUsDateFormat = (date) => {
  if (date) {
    const parts = date.split(/-/)
    return `${parseInt(parts[1])}/${parseInt(parts[2])}/${parseInt(parts[0])}`
  }
  return null
}

const usDateFormatToShortMonthAndDay = (date) => {
  if (date) {
    const parts = date.split('/').map((num) => +num)
    const month = parts[0]
    const day = parts[1]

    return `${shortMonths[month - 1]} ${day}`
  }
  return null
}

const isUnder18FromYYYYMMDD = (dob) => {
  if (!dob) return false
  const birth = new Date(`${dob}T00:00:00`)
  if (Number.isNaN(birth.getTime())) return false

  const today = new Date()
  let age = today.getFullYear() - birth.getFullYear()
  const m = today.getMonth() - birth.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1
  return age < 18
}

// Use this for birth dates
const dobToYYYYMMDDSafe = (value) => {
  if (!value) return null

  const s = String(value).trim()

  // already ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s

  // US format, Canvas sends dob as M/d/yy
  if (!/^\d{1,2}\/\d{1,2}\/(\d{2}|\d{4})$/.test(s)) return null

  // 2-digit years after this year are 1900s
  return usDateFormatToYYYYMMDD(s, 2000, false, new Date().getFullYear() % 100)
}

const usDateFormatToYYYYMMDD = (date, centuryOffset = 2000, forceOffset = false, compareYear = 30) => {
  if (date) {
    const [monthStr, dayStr, yearStr] = date.split('/')
    const month = +monthStr
    const day = +dayStr
    let year = +yearStr

    if (yearStr?.length !== 4) {
      if (forceOffset) {
        year += centuryOffset
      } else if (year > compareYear) {
        // XXX
        // assume 1900s
        year += 1900
      } else {
        // assume 2000s
        year += centuryOffset
      }
    }

    return sprintf('%04d-%02d-%02d', year, month, day)
  }
  return null
}

const getNumberOfDays = (start, end) => {
  const date1 = new Date(start)
  const date2 = new Date(end)

  // One day in milliseconds
  const oneDay = 1000 * 60 * 60 * 24

  // Calculating the time difference between two dates
  const diffInTime = date2.getTime() - date1.getTime()

  // Calculating the no. of days between two dates
  const diffInDays = Math.round(diffInTime / oneDay)

  return diffInDays
}

const getDatePartsInSiteTimezone = (date) => {
  if (!date) return null

  // US date format (M/D/YYYY or M/D/YY) — parse parts directly to avoid
  // new Date() interpreting a bare date string as browser-local midnight,
  // which would shift the displayed date by one day for users east of the
  // site's timezone (e.g. Eastern viewers of a Mountain-time site).
  const usMatch = String(date).match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (usMatch) {
    let year = parseInt(usMatch[3], 10)
    if (usMatch[3].length === 2) year += 2000
    return { year, month: parseInt(usMatch[1], 10), day: parseInt(usMatch[2], 10) }
  }

  const dateObj = new Date(date)
  if (Number.isNaN(dateObj.getTime())) return null

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: siteConfig.timezone || 'America/New_York',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  })
  const parts = formatter.formatToParts(dateObj)
  const values = parts.reduce((acc, part) => {
    if (part.type !== 'literal') {
      acc[part.type] = Number(part.value)
    }
    return acc
  }, {})

  return {
    year: values.year,
    month: values.month,
    day: values.day,
  }
}

const timestampToDateText = (date) => {
  const parts = getDatePartsInSiteTimezone(date)
  if (!parts) return null

  return `${months[parts.month - 1]} ${parts.day}, ${parts.year}`
}

const timestampToShortDateText = (date) => {
  const parts = getDatePartsInSiteTimezone(date)
  if (!parts) return null

  return `${shortMonths[parts.month - 1]} ${parts.day}`
}

const timestampsToDateRange = (startDateTimestamp, endDateTimestamp) => {
  const starts = getDatePartsInSiteTimezone(startDateTimestamp)
  const ends = getDatePartsInSiteTimezone(endDateTimestamp)
  if (!starts || !ends) return null

  const startMonth = months[starts.month - 1]
  const endMonth = months[ends.month - 1]

  if (startMonth === endMonth) {
    if (starts.day === ends.day && starts.year === ends.year) {
      return `${startMonth} ${starts.day}, ${starts.year}`
    }
    return `${startMonth} ${starts.day} - ${ends.day}, ${ends.year}`
  }

  return `${startMonth} ${starts.day} - ${endMonth} ${ends.day}, ${ends.year}`
}
function convert24to12(time24) {
  const [hours, minutes] = time24.split(':')
  const period = +hours < 12 ? 'AM' : 'PM'
  const newHours = (+hours % 12 || 12).toString()
  return `${newHours.padStart(2, '0')}:${minutes} ${period}`
}

const toLocalDate = (value) => {
  const iso = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (iso) return new Date(+iso[1], iso[2] - 1, +iso[3])
  return new Date(value)
}

const getAgeAtDate = (dobDateString, comparisonDateString) => {
  const dobDate = toLocalDate(dobDateString)
  const comparisonDate = toLocalDate(comparisonDateString)
  const ageAtComparison = comparisonDate.getFullYear() - dobDate.getFullYear()
  const monthDiff = comparisonDate.getMonth() - dobDate.getMonth()

  // Adjust age if birthday hasn't occurred yet in the comparison year
  return monthDiff < 0 || (monthDiff === 0 && comparisonDate.getDate() < dobDate.getDate())
    ? ageAtComparison - 1
    : ageAtComparison
}

// 'ok' | 'warn' (outside the class range, but inside marginYears) | 'block'
const getClassAgeEligibility = (dob, cls, marginYears = 0) => {
  const startDate = cls?.startDateStandard || cls?.startDate
  if (!dob || !startDate || !cls.minAge || !cls.maxAge) return 'ok'

  const dobStandard = dobToYYYYMMDDSafe(dob)
  if (!dobStandard) return 'block'

  const age = getAgeAtDate(dobStandard, startDate)
  if (age >= cls.minAge && age <= cls.maxAge) return 'ok'

  const margin = Number(marginYears)
  const grace = Number.isFinite(margin) && margin > 0 ? margin : 0
  if (grace && age >= cls.minAge - grace && age <= cls.maxAge + grace) return 'warn'

  return 'block'
}

const isClassAgeEligible = (dob, cls) => getClassAgeEligibility(dob, cls, 0) !== 'block'

const getDateStandard = (dateString = null) => {
  let date = dateString ? new Date(dateString) : new Date()
  let dateStandard = date.toLocaleDateString('en-CA', { timeZone: siteConfig.timezone || 'America/New_York' })
  return dateStandard
}

const getYearsByUnit = (value, unit) => {
  // expected unit values are - month, year

  if (!value) return 0
  return unit === 'month' ? value / 12 : value
}

export {
  days,
  months,
  dateToYYYYMMDD,
  timestampToDateText,
  timestampToShortDateText,
  timestampsToDateRange,
  usDateFormatToShortMonthAndDay,
  usDateFormatToYYYYMMDD,
  dobToYYYYMMDDSafe,
  isUnder18FromYYYYMMDD,
  yyyymmddToUsDateFormat,
  getNumberOfDays,
  convert24to12,
  getAgeAtDate,
  getClassAgeEligibility,
  isClassAgeEligible,
  getDateStandard,
  getYearsByUnit,
}
